import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { IonicModule, ModalController } from '@ionic/angular';
import { Observable, Subscription } from 'rxjs';
import { AnalyseDataService } from 'src/app/services/analyse/analyse-data.service';
import { Prediction } from 'src/app/services/analyse/analyse.types';
import { AnalyticsService } from 'src/app/services/analytics/analytics.service';

@Component({
  selector: 'app-prediction-nettleie',
  standalone: true,
  styleUrls: ['./prediction.component.scss'],
  imports: [CommonModule, IonicModule],
  template: `
    <div class="modalClose">
      <img src="/assets/img/shared/close.svg" (click)="closeModal()" />
    </div>
    <ion-title class="smaller">
      <h1>Når bruker du mest strøm samtidig?</h1>
    </ion-title>
    <p class="text-sm m-0">
      Beregningene er basert på ditt forbruk de siste 30 dagene.
    </p>
    @if (isLoading && !errorFetching) {
    <div class="m-2">
      <p>Vi kalkulerer...</p>
      <ion-spinner name="dots"></ion-spinner>
    </div>
    } @else if (errorFetching) {
    <div class="m-2">
      <p>
        Det skjedde en feil når vi prøvde å kalkulere analysen. Prøv igjen
        senere eller kontakt kundeservice om problemet vedvarer.
      </p>
    </div>
    } @else {
    <div class="m-4">
      <div>
        <p class="my-2">
          <strong class="block">⚡️ {{ highestDayHour }}</strong>
          <i class="block">
            Dette tidspunktet tror vi du bruker aller mest strøm samtidig.
          </i>
        </p>
      </div>
      <div class="flex flex-col justify-center">
        <p>
          <strong class="block">🗓️ {{ highestDay }}</strong>
          <i class="block">
            Denne ukedagen tror vi du bruker mest strøm samtidig hele døgnet
            gjennom en vanlig uke.
          </i>
        </p>
        <p>
          <strong class="block"
            >🕰️ {{ highestHour }} (kl. {{ hourTime }})</strong
          >
          <i class="block">
            Dette tidsrommet tror vi du bruker mest strøm samtidig på en vanlig
            dag i uka.
          </i>
        </p>
        <p class="text-lg bold mb-2">Fordeling av høyeste forbruk</p>
        <table>
          <tr>
            <th></th>
            <th
              *ngFor="let time of times"
              class="capitalize whitespace-pre-line"
            >
              {{ time }}
            </th>
          </tr>
          <tr *ngFor="let i = index; let row; of: valueMatrix">
            <td class="bold">{{ days[i] }}</td>
            <td
              *ngFor="let value of row"
              [ngStyle]="{ '--distribution': value * 4 }"
              class="td-bg text-sm"
            >
              <!-- {{ value ? (value * 100).toFixed(0) + ' %' : '' }} -->
            </td>
          </tr>
        </table>
        <p class="text-sm my-2">Skala fra lite til mye samtidig strømforbruk</p>
        <p class="text-sm m-0 grid grid-cols-[auto_1fr_auto] gap-2">
          <span>Lite</span>
          <span class="w-full h-6 scale-desc"></span>
          <span>Mye</span>
        </p>
        <p>
          Når du har høye døgnmakser er det oftest i de uthevede periodene de
          inntreffer, basert på vår analyse. Kan du ta noen grep for å fordele
          strømforbruket ditt mer utover døgnet?
        </p>
        <p>
          Tingene som normalt krever mest strøm er lading av elbil, bruk av
          varmtvann, panelovner og kokeplater. Jo mindre av dette man kan bruke
          samtidig, jo bedre.
        </p>
      </div>
    </div>
    }
  `
})
export class PredictionNettleieComponent {
  predictionResult$: Observable<Prediction>;
  subscription: Subscription;
  isLoading: boolean;
  errorFetching: boolean;

  highestDay: string;
  highestHour: string;
  hourTime: string;
  highestDayHour: string;

  valueMatrix: number[][];
  times: string[];
  days: string[];

  constructor(
    private _analyseDataService: AnalyseDataService,
    private _analytics: AnalyticsService,
    private _modalCtrl: ModalController
  ) {
    this.predictionResult$ = this._analyseDataService.prediction$;
  }

  ngOnInit() {
    this._analytics.logEvent_selectContent('analysis_open_daymax_distribution');
    this.subscription = this.predictionResult$.subscribe({
      next: (data) => {
        this.highestDay = data.daily.reduce((prev, current) =>
          prev.value > current.value ? prev : current
        ).category;
        const hourCategory = data.hourly.reduce((prev, current) =>
          prev.value > current.value ? prev : current
        ).category;
        this.highestHour =
          hourCategory === 'dag'
            ? 'Dagtid'
            : hourCategory === 'kveld'
            ? 'Ettermiddag/kveld'
            : 'Natt';
        this.hourTime =
          hourCategory === 'dag'
            ? '06-14'
            : hourCategory === 'kveld'
            ? '14-22'
            : '22-06';

        const dayHourValues = data.daily_hourly
          .flatMap((day) => day.values.map((hour) => hour.value))
          .filter((value) => value > 0);

        if (
          dayHourValues.filter((value) => value === Math.max(...dayHourValues))
            .length > 1
        ) {
          this.highestDayHour = 'Flere tidspunkter';
        } else {
          const dayHourMax = data.daily_hourly.reduce((prev, current) =>
            prev.values.reduce((p, c) => (p.value > c.value ? p : c)).value >
            current.values.reduce((p, c) => (p.value > c.value ? p : c)).value
              ? prev
              : current
          );
          const dayHourMaxValue = dayHourMax.values.reduce((prev, current) =>
            prev.value > current.value ? prev : current
          ).category;

          this.highestDayHour = `${dayHourMax.category} på ${
            dayHourMaxValue === 'dag'
              ? 'dagtid'
              : dayHourMaxValue === 'kveld'
              ? 'ettermiddag/kveld'
              : 'natten'
          }`;
        }

        this.valueMatrix = data.daily_hourly.map((day) =>
          day.values.map((hour) => hour.value)
        );
        this.days = data.daily.map((day) => day.category);
        this.times = data.hourly
          .map((hour) => hour.category)
          .map((x) =>
            x === 'dag'
              ? 'Dag\n(06-14)'
              : x === 'kveld'
              ? 'Kveld\n(14-22)'
              : 'Natt\n(22-06)'
          );
        this.isLoading = false;
      },
      error: () => {
        this.errorFetching = true;
        this.isLoading = false;
      }
    });
    this.isLoading = true;
    this._analyseDataService.fetchChemX();
  }

  closeModal() {
    this._modalCtrl.dismiss();
  }
}
